import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { FaCircle } from "react-icons/fa";
import { useNavigate } from "react-router";
import { CommonContext } from "../../App";
import { SiGoogletagmanager } from "react-icons/si";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import api from "../../services/api";

export default function UtilisationReport() {
    const { t } = useTranslation();
    const [branchSheets, setBranchSheets] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});

    const navigate = useNavigate();

    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];
    const currentYear = today.getFullYear();
    const aprilFirstCurrentYear = new Date(`${currentYear}-04-01`);
    const defaultStartYear =
        today >= aprilFirstCurrentYear ? currentYear : currentYear - 1;

    const { branchId, dateRange } = useContext(CommonContext);
    const [startDate, setStartDate] = useState(dateRange.startDate);
    const [endDate, setEndDate] = useState(dateRange.endDate);
    const [startDateManuallyChanged] = useState(false);
    const storedLang = JSON.parse(localStorage.getItem("second_language"));
    const language = storedLang?.language || "en";
    useEffect(() => {
        setStartDate(dateRange.startDate);
        setEndDate(dateRange.endDate);
    }, [dateRange]);

    const [loading, setLoading] = useState(false);

    // ======================= FETCH TRIAL BALANCE ======================
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const res = await api.get(`ledgers/get-trial-summary`, {
                    params: { startDate, endDate, branchId },
                });

                if (res.data.success) {
                    const branchSheetsTemp = res.data.data.map((branchBlock) => {
                        const { branchId, branchName, branchData } = branchBlock;

                        const tableRows = [];
                        const tableplRows = [];

                        branchData.forEach((ledger) => {
                            // const debit = Math.max(ledger.totalReceived || 0, 0);
                            // const credit = Math.max(ledger.totalPaid || 0, 0);
                            // Opening effect
                            const openingEffect =
                                ledger.openingBalanceType === "received"
                                    ? Number(ledger.openingBalance || 0)
                                    : -Number(ledger.openingBalance || 0);

                            // Current movement
                            const movement =
                                Number(ledger.totalReceived || 0) -
                                Number(ledger.totalPaid || 0);

                            // Final net balance
                            const net = openingEffect + movement;

                            const debit = net > 0 ? net : 0;
                            const credit = net < 0 ? Math.abs(net) : 0;
                            const baseRow = {
                                id: ledger.id,
                                account: ledger.name,
                                nameInHindi: ledger.nameInHindi,
                                groupName: ledger.groupName || ledger.name,
                                groupNameInHindi: ledger.groupNameInHindi,
                                subGroupName: ledger.subGroupName,
                                subGroupNameInHindi: ledger.subGroupNameInHindi,
                                type: ledger.groupType,
                                debit,
                                credit,
                                date: ledger.date,
                                branchId,
                                branchName,
                            };

                            if (["Assets", "Liability", "Equity"].includes(ledger.groupType)) {
                                tableRows.push(baseRow);
                            }

                            // if (["Income", "Expense"].includes(ledger.groupType)) {
                            //     tableplRows.push(baseRow);
                            // }
                        });

                        return {
                            branchId,
                            branchName,
                            trialBalanceRows: tableRows,
                            // plBalanceRows: tableplRows,
                        };
                    });

                    setBranchSheets(branchSheetsTemp);
                }
            } catch (error) {
                console.error("Error fetching trial balance:", error);
            } finally {
                setLoading(false);
            }
        };

        if (startDate && endDate && branchId) {
            fetchData();
        }


    }, [startDate, endDate, branchId]);

    // =================== DATE MANAGE ===================
    // useEffect(() => {
    //     if (startDateManuallyChanged) return;
    //     const end = new Date(endDate);
    //     const endYear = end.getFullYear();
    //     const aprilFirstOfEndYear = new Date(`${endYear}-04-01`);
    //     const adjustedStartYear = end < aprilFirstOfEndYear ? endYear - 1 : endYear;
    //     setStartDate(`${adjustedStartYear}-04-01`);
    // }, [endDate, startDateManuallyChanged]);

    const filterByDate = (rows) => {
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        return rows.filter((r) => {
            if (!r.date) return true;
            const rowDate = new Date(r.date);
            if (start && rowDate < start) return false;
            if (end && rowDate > end) return false;
            return true;
        });
    };

    const structureRows = (rows) => {
        const data = {};
        rows.forEach((r) => {
            const group = r.groupName || r.account;
            if (!data[group]) {
                data[group] = {
                    groupName: group,
                    groupNameInHindi: r.groupNameInHindi || r.nameInHindi,
                    groupType: r.type,
                    subGroups: {},
                    totalDebit: 0,
                    totalCredit: 0,
                };
            }

            data[group].totalDebit += r.debit;
            data[group].totalCredit += r.credit;

            if (r.subGroupName) {
                if (!data[group].subGroups[r.subGroupName]) {
                    data[group].subGroups[r.subGroupName] = {
                        subGroupName: r.subGroupName,
                        subGroupNameInHindi: r.subGroupNameInHindi,
                        groupType: r.type,
                        debit: 0,
                        credit: 0,
                        accounts: [],
                    };
                }
                data[group].subGroups[r.subGroupName].debit += r.debit;
                data[group].subGroups[r.subGroupName].credit += r.credit;
                data[group].subGroups[r.subGroupName].accounts.push(r);
            }
        });
        return data;
    };

    const toggleGroup = (group) => {
        setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    };
    const safeNum = (v) => {
        if (v === null || v === undefined) return 0;
        if (typeof v === "string") return Number(v.replace(/,/g, "")) || 0;
        if (typeof v === "number") return v;
        return 0;
    };

    const totalDebit = (rows) =>
        Object.values(rows)
            .reduce((sum, g) => sum + safeNum(g.totalDebit), 0)
            ;

    const totalCredit = (rows) =>
        Object.values(rows)
            .reduce((sum, g) => sum + safeNum(g.totalCredit), 0)
            ;
    const formatAmount = (num) =>
        Math.abs(num).toLocaleString("en-IN");


    const renderTable = (structuredData) => (
        <table className="w-full border-collapse text-sm">
            <thead>
                <tr className=" text-theme-headerBg border-theme-headerBg  ">
                    <th className="px-1 border-b py-[2px] font-semibold text-left">{t("ledger/account")}</th>
                    <th className="px-1 border-b py-[2px] font-semibold text-right">{t("paid")} (₹)</th>
                    <th className="px-1 border-b py-[2px] font-semibold text-right">{t("received")} (₹)</th>
                    <th className="px-1 border-b py-[2px] font-semibold text-right">{t("net")} (₹)</th>
                </tr>
            </thead>
            <tbody>

                {Object.keys(structuredData).length === 0 ? (
                    <tr>
                        <td
                            colSpan={4}
                            className="text-center py-3 text-gray-400 font-semibold"
                        >
                            {t("noDataAvailable")}
                        </td>
                    </tr>
                ) : (
                    Object.entries(structuredData).map(([groupName, gData]) => {
                        const hasSubGroups = Object.keys(gData.subGroups).length > 0;
                        const isExpanded = expandedGroups[groupName];

                        return (
                            <React.Fragment key={groupName}>
                                <tr
                                    className=" bg-theme-tableBg hover:bg-theme-tableHoverColor  text-sm cursor-pointer"
                                    onClick={() => hasSubGroups && toggleGroup(groupName)}
                                >
                                    <td className="py-[2px] border-b px-1  flex items-center">
                                        {groupName} {language !== "en" && (gData.groupNameInHindi)}
                                        {hasSubGroups && (
                                            <span
                                                className={`text-[9.5px] ml-2 transform transition-transform duration-300 ${isExpanded ? "rotate-90" : ""
                                                    }`}
                                            >
                                                {isExpanded ? "▲" : "▼"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-1 border-b text-right">
                                        {gData.totalDebit === 0 ? "" : gData.totalDebit}
                                    </td>
                                    <td className="px-1 border-b text-right">
                                        {gData.totalCredit === 0 ? "" : gData.totalCredit}
                                    </td>
                                    <td className="px-1 border-b text-right">
                                        {(gData.totalDebit > gData.totalCredit) ? (gData.totalDebit - gData.totalCredit)
                                            : (gData.totalCredit - gData.totalDebit)}  {(gData.totalDebit > gData.totalCredit) ? "Dr"
                                                : "Cr"}
                                    </td>
                                </tr>

                                {isExpanded &&
                                    Object.entries(gData.subGroups).map(([subName, vals]) => (
                                        <tr key={subName} className="bbg-theme-tableBg hover:bg-theme-tableHoverColor text-sm">
                                            <td className="px-5 border-b py-[2px] pl-6 flex items-center gap-1 ps-5">
                                                <div className="text-[7px] mt-[2px] font-bold">
                                                    <FaCircle />
                                                </div>
                                                <div
                                                    className="cursor-pointer"
                                                    onClick={subName === 'difference in opening balance' ? undefined:() =>
                                                        navigate(`/account-books/ledgers-table`, {
                                                            state: {
                                                                ledger: gData,
                                                                btnHeading: "P/L Utilisation",
                                                                btnlink: "/reports/profit-loss/utilisation",
                                                            },
                                                        })
                                                    }
                                                >
                                                    {subName} {language !== "en" && (vals.subGroupNameInHindi)}
                                                </div>
                                            </td>
                                            <td className="px-1 border-b text-right">
                                                {vals.debit === 0 ? "" : vals.debit}
                                            </td>
                                            <td className="px-1 border-b text-right">
                                                {vals.credit === 0 ? "" : vals.credit}
                                            </td>
                                            <td className="px-1 border-b text-right">
                                                {(vals.debit > vals.credit) ? (vals.debit - vals.credit) : (vals.credit - vals.debit)}   {(vals.debit > vals.credit) ? "Dr" : "Cr"}
                                            </td>
                                        </tr>
                                    ))}
                            </React.Fragment>
                        );
                    }))}


            </tbody>
            {Object.keys(structuredData).length !== 0 && (<tfoot>
                <tr className="font-medium text-sm text-theme-headerBg  ">
                    <td className="px-1 pt-1">{t("total")}</td>
                    <td className="px-1 pt-1 text-right">{totalDebit(structuredData)}</td>
                    <td className="px-1 pt-1 text-right">{totalCredit(structuredData)}</td>
                    <td className="px-1 pt-1 text-right">
                        {formatAmount(
                            Math.abs(
                                totalDebit(structuredData) - totalCredit(structuredData)
                            )
                        )}{" "}
                        {totalDebit(structuredData) > totalCredit(structuredData)
                            ? "Dr"
                            : "Cr"}
                    </td>
                </tr>
            </tfoot>)}
        </table>
    );


    return (
        <div className=" mt-2 bg-white shadow-sm rounded-1 p-2">

            <div className="flex justify-between border-b bg-theme-headerBg text-theme-color rounded-1 px-2 py-1 mb-1">
                <div className=" font-medium text-center ">{t("profitLossUtilisation")}</div>
            </div>

            {loading ? (
                <div className="text-center py-10">
                    <LoadingSpinner size={70} color="#000000" />
                </div>
            ) : (
                branchSheets.map((branch) => {
                    // Filter rows
                    const filteredTrial = filterByDate(branch.trialBalanceRows);
                    // const filteredPL = filterByDate(branch.plBalanceRows);

                    // Divide types
                    const assetsRows = filteredTrial.filter((r) => r.type === "Assets");
                    const liabilitiesRows = filteredTrial.filter(
                        (r) => r.type === "Liability"
                    );
                    const equityRows = filteredTrial.filter((r) => r.type === "Equity");


                    const equityWithProfit = [...equityRows];


                    const structuredAssets = structureRows(assetsRows);
                    const structuredLiabilities = structureRows([
                        ...liabilitiesRows,
                        ...equityWithProfit,
                    ]);

                    const totalAssetsNet =
                        Math.abs(
                            totalDebit(structuredAssets) - totalCredit(structuredAssets)
                        );

                    const totalLiabilitiesNet =
                        Math.abs(
                            totalDebit(structuredLiabilities) -
                            totalCredit(structuredLiabilities)
                        );

                    return (
                        <div key={branch.branchId} className="mb-2">
                            <div className="  mb-2  flex justify-center gap-1 font-serif px-1">

                                <div className="capitalize border-b text-theme-headerBg border-theme-headerBg">{branch.branchName}</div>
                            </div>

                            {/* ❗ NEW ADDED: show NO DATA message */}
                            {!loading && branch.trialBalanceRows && branch.trialBalanceRows.length === 0 &&
                                branch.plBalanceRows && branch.plBalanceRows.length === 0 ? (
                                <div className="text-center  text-red-500 text-sm py-4">
                                    {t("noDataAvailable")}
                                </div>
                            ) : (
                                <>
                                    <div className="flex gap-2">
                                        <div className="w-1/2">
                                            <div className="text-sm font-semibold  px-2 py-1 text-theme-color  bg-theme-headerBg">
                                                {t("liability&Equity")}
                                            </div>
                                            {renderTable(structuredLiabilities)}
                                        </div>

                                        <div className="w-1/2">
                                            <div className="text-sm font-semibold  px-2 py-1  text-theme-color  bg-theme-headerBg">
                                                {t("assets")}
                                            </div>
                                            {renderTable(structuredAssets)}
                                        </div>
                                    </div>

                                    {totalAssetsNet != totalLiabilitiesNet ?
                                        <div
                                            className={`flex justify-end border-t border-theme-headerBg w-full font-bold mt-2 ${totalAssetsNet > totalLiabilitiesNet
                                                ? "text-green-700"
                                                : "text-red-600"
                                                }`}
                                        >
                                            {/* <div className="w-1/2 px-3">
                                            <div className="border-t flex justify-between pt-2 px-1">
                                                <div>
                                                    {t("total")} {t("liability&Equity")}
                                                </div>
                                                <div>{totalLiabilitiesNet.toLocaleString("en-IN")}</div>
                                            </div>
                                        </div> */}

                                            <div className="">
                                                <div className=" flex  gap-2 pt-1 text-sm px-1">
                                                    <div>

                                                        {totalAssetsNet > totalLiabilitiesNet ? t("netProfit") : t("netLoss")} (₹)	 :
                                                    </div>
                                                    <div>
                                                        {Math.abs(totalAssetsNet - totalLiabilitiesNet).toLocaleString("en-IN")}{" "}
                                                    </div>
                                                </div>
                                            </div>
                                        </div> : <div
                                            className={`flex justify-end border-t border-theme-headerBg w-full font-bold mt-1 text-green-700`}
                                        >
                                            {/* <div className="w-1/2 px-3">
                                            <div className="border-t flex justify-between pt-2 px-1">
                                                <div>
                                                    {t("total")} {t("liability&Equity")}
                                                </div>
                                                <div>{totalLiabilitiesNet.toLocaleString("en-IN")}</div>
                                            </div>
                                        </div> */}

                                            <div className="">
                                                <div className=" flex  gap-2 pt-1 text-sm px-1">
                                                    <div>

                                                        {t("netProfit")} (₹)	 :
                                                    </div>
                                                    <div>
                                                        0
                                                    </div>
                                                </div>
                                            </div>
                                        </div>}
                                </>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
}
